import { useLang } from "@/lib/lang";
import { ArrowUpRight } from "lucide-react";
import { LogoMark } from "./Logo";
import { Eyebrow, MaskLines, Reveal } from "./motion";

const LOGOS = [
  { src: "/img/partners/hagag-group.png", alt: "Hagag Group logo", url: "https://www.hagag-group.co.il/", cls: "p-[9%]" },
  { src: "/img/partners/jacob-hotels.svg", alt: "Jacob Hotels logo", url: "https://jacobhotels.com/", cls: "px-[19%] py-[12%]" },
  { src: null, alt: "OGEN Hospitality", url: null },
];

const LogoTile = ({ logo, i }) => {
  const inner = (
    <span className="relative grid aspect-[4/3] w-full place-items-center overflow-hidden rounded-[24px] bg-[#0C3440] shadow-[0_24px_50px_-30px_rgba(14,95,115,0.7)] transition-transform duration-700 group-hover:-translate-y-1">
      <span className="absolute inset-0 bg-[radial-gradient(60%_60%_at_50%_40%,rgba(127,217,214,0.18),transparent_70%)]" />
      {logo.src ? (
        <span className={`absolute inset-0 flex items-center justify-center ${logo.cls}`}><img src={logo.src} alt={logo.alt} loading="lazy" className="max-h-full max-w-full object-contain" /></span>
      ) : (
        <span className="relative flex flex-col items-center gap-2 text-white" dir="ltr">
          <LogoMark className="h-14 w-14 text-[#7FD9D6]" />
          <span className="font-display text-2xl tracking-[0.2em]">OGEN</span>
          <span className="text-[10px] uppercase tracking-[0.4em] text-white/80">Hospitality</span>
        </span>
      )}
    </span>
  );
  return logo.url ? (
    <a href={logo.url} target="_blank" rel="noopener noreferrer" className="block" data-testid={`partner-logo-link-${i}`} aria-label={logo.alt}>{inner}</a>
  ) : (
    <div data-testid={`partner-logo-${i}`}>{inner}</div>
  );
};

export const Partners = () => {
  const { t } = useLang();
  return (
    <section id="partners" className="relative mx-auto max-w-[1440px] px-5 md:px-10 py-24 md:py-36" data-testid="partners-section">
      <Eyebrow>{t.partners.eyebrow}</Eyebrow>
      <h2 className="mt-6 max-w-4xl font-display text-[clamp(2.4rem,4.8vw,4.4rem)] leading-[1] text-[var(--ink)]">
        <MaskLines key={t.partners.title} lines={[t.partners.title]} />
      </h2>
      <div className="mt-14 md:mt-20">
        {t.partners.list.map((p, i) => (
          <Reveal key={i} className="group relative grid gap-6 border-t border-[var(--line)] py-8 md:py-10 md:grid-cols-12 md:items-center md:gap-8 last:border-b" data-testid={`partner-${i}`}>
            <div className="md:col-span-3 max-w-[320px]"><LogoTile logo={LOGOS[i]} i={i} /></div>
            <div className="md:col-span-4">
              <p className="font-display text-4xl md:text-5xl leading-none text-[var(--ink)] transition-transform duration-700 group-hover:translate-x-2 rtl:group-hover:-translate-x-2">{p.name}</p>
              <p className="mt-4 text-[12px] uppercase tracking-[0.24em] text-[var(--brass-ink)]">{p.role}</p>
              {LOGOS[i].url && (
                <a href={LOGOS[i].url} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-1 text-sm text-[var(--ink-soft)] hover:text-[var(--ink)] link-line" dir="ltr" data-testid={`partner-site-link-${i}`}>
                  {LOGOS[i].url.replace(/^https:\/\/(www\.)?/, "").replace(/\/$/, "")}<ArrowUpRight className="h-3.5 w-3.5" />
                </a>
              )}
            </div>
            <p className="md:col-span-5 text-[15px] leading-relaxed text-[var(--ink-soft)]">{p.body}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
};
